import React from 'react';
import { AlertTriangle, Building2, Landmark, ShieldAlert, RefreshCcw } from 'lucide-react';
import { clsx } from 'clsx';
import { TOOLBAR_FOCUS } from './WorkbenchToolbar';

interface TriageSummary {
    risk_score?: number;
    risk_level?: string;
    entity_name?: string;
    sanctioned?: boolean;
    sanctions_entity?: string;
    nearest_exchange?: {
        name?: string;
        hops?: number;
    } | null;
    p2p_hits?: number;
}

interface TriagePayload {
    address: string;
    chain: string;
    summary?: TriageSummary;
}

interface TriageCardProps {
    triage: TriagePayload | null;
    loading: boolean;
    error?: string | null;
    fallbackAddress?: string;
    fallbackChain?: string;
    onRefresh: () => void;
    onExpandTransactions: () => void;
    onSanctionsScreen: () => void;
    onAutoTrace: () => void;
    /** inspector = docked in Node Inspector; overlay = legacy canvas float (deprecated) */
    variant?: 'inspector' | 'overlay';
}

const actionBtnClass = clsx(
    TOOLBAR_FOCUS,
    'inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border bg-secondary/90 text-foreground transition-colors hover:bg-muted/80 hover:border-primary/40',
);

export const TriageCard: React.FC<TriageCardProps> = ({
    triage,
    loading,
    error,
    onRefresh,
    onExpandTransactions,
    onSanctionsScreen,
    onAutoTrace,
    variant = 'inspector',
}) => {
    const summary = triage?.summary || {};
    const isInspector = variant === 'inspector';

    if (isInspector && !loading && !error && !triage) return null;

    return (
        <div
            data-testid="triage-card"
            className={clsx(
                isInspector
                    ? 'mt-2 pt-2 border-t border-border/50 flex flex-col gap-2'
                    : 'absolute top-3 left-3 right-3 md:right-auto md:max-w-md z-10 rounded-lg border border-border bg-card/95 backdrop-blur-md shadow-overlay px-3 py-2',
            )}
        >
            <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Quick actions
                </p>
                <button
                    type="button"
                    onClick={onRefresh}
                    className={actionBtnClass}
                    disabled={loading}
                    title="Refresh triage snapshot for this address"
                >
                    <RefreshCcw className={clsx('w-3.5 h-3.5', loading && 'animate-spin')} />
                    Refresh
                </button>
            </div>

            {error && (
                <div className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Triage failed: {error}
                </div>
            )}

            {summary.sanctioned && summary.sanctions_entity && (
                <div className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Sanctions match: {summary.sanctions_entity}
                </div>
            )}

            <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={onExpandTransactions} className={actionBtnClass} title="Load transfers for this address">
                    <Building2 className="w-3.5 h-3.5" />
                    Expand transfers
                </button>
                <button type="button" onClick={onSanctionsScreen} className={actionBtnClass} title="Run OFAC sanctions screening">
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Screen OFAC
                </button>
                <button type="button" onClick={onAutoTrace} className={actionBtnClass} title="Trace fund path to nearest KYC exchange">
                    <Landmark className="w-3.5 h-3.5" />
                    Trace to exchange
                </button>
            </div>
        </div>
    );
};

export default TriageCard;
